/**
 * RESPONSIBILITY: rows of a repeatable, at any depth.
 *
 * What binds the outermost list to JSONForms is list.tsx; what one open row contains is
 * row-fields.tsx. This is here rather than in either of them because a list inside a row
 * must behave exactly like the list containing it — the same summary, the same buttons,
 * the same add. Two implementations of "a repeatable" is how the inner one ends up the
 * poor relation.
 *
 * A ROW IS COLLAPSED. Eight events of fifteen fields each is four screens of inputs to
 * scroll past to reach the ninth; closed, it is a list of names you open one of. The
 * summary is the first visible column that holds anything, because the first column is
 * what an editor named the thing — title, question, name — so the list reads as itself
 * without being told which field to use.
 */
import { ROW_ID, newRowId } from '@framework/core';
import type { ItemField } from './cell-types';
import { RowFields } from './row-fields';
import { visible } from './show-when';

/**
 * What a PICTURE is called, for a row that is nothing but one.
 *
 * A list of stills has no text column at all, so every row read "Untitled — open to fill
 * in" and the list was three identical lines for three different photographs. The alt
 * text is what the editor typed about it; the file name is what they chose. Either beats
 * a row that cannot be told from its neighbours.
 */
function pictureName(v: object): string {
  const img = v as { path?: unknown; alt?: unknown };
  if (typeof img.path !== 'string') return '';
  if (typeof img.alt === 'string' && img.alt.trim()) return img.alt.trim();
  return img.path.split('/').pop() ?? '';
}

export interface RepeatableProps {
  /** Unique within the form. Cell ids are built from it, so it must not collide. */
  path: string;
  fields: ItemField[];
  rows: Record<string, unknown>[];
  onChange: (next: Record<string, unknown>[]) => void;
  /** The section document. A cell with `in` resolves its vocabulary against this. */
  root: Record<string, unknown>;
  /** Fixed vocabularies out of the JSON Schema, by column key. */
  enums?: Record<string, string[] | undefined>;
  maxItems?: number;
  /** The word on the add button, when "Add" is not what this list adds. */
  addLabel?: string;
}

/**
 * What the closed row says: its first two columns that hold anything.
 *
 * TWO and not one. A row whose first column is a fixed vocabulary reads as a list of the
 * same six words repeated — eight bands all called "Body" is a list you have to open
 * every item of to navigate. The second value is what tells them apart, and where there
 * is no second value the row is exactly as it was.
 *
 * Empty rows are the ones just added, so say so.
 */
const summaryOf = (row: Record<string, unknown>, fields: ItemField[]): string => {
  const parts: string[] = [];

  for (const f of fields.filter((x) => visible(x, row))) {
    const v = row[f.key];
    if (v === undefined || v === null) continue;
    const text = Array.isArray(v)
      ? v.filter((x) => typeof x === 'string').join(', ')
      : typeof v === 'object' ? pictureName(v) : String(v).trim();
    if (text) parts.push(text);
    if (parts.length === 2) break;
  }

  return parts.join(' · ') || 'Untitled — open to fill in';
};

/**
 * What a NEW row holds, per column, from the annotation.
 *
 * Every field used to start as `''`, which is a lie for four of the kinds and a
 * destructive one for two: an empty string where a list belongs hands the nested control
 * something it cannot draw, and one where an image belongs makes `row.image.path` throw
 * on the page. The annotation already says what the column is; this asks it.
 */
function blank(f: ItemField): unknown {
  // What the annotation said, if it said anything. A dropdown declaring a default and
  // then starting outside its own list is the row that cannot be saved.
  if (f.default !== undefined) return f.default;

  switch (f.kind) {
    case 'list':
    case 'stringlist':
      return [];
    case 'choice':
      return f.multiple ? [] : '';
    case 'img':
      return { path: '', alt: '' };
    case 'bool':
      return false;
    // Undefined and not 0: an untouched number must not save as a number nobody typed.
    case 'num':
      return undefined;
    default:
      return '';
  }
}

export function Repeatable({
  path, fields, rows, onChange, root, enums = {}, maxItems, addLabel = 'Add',
}: RepeatableProps) {
  const patch = (i: number, k: string, v: unknown) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const move = (i: number, d: -1 | 1) => {
    const next = [...rows];
    const t = next[i + d];
    if (!t) return;
    next[i + d] = next[i]; next[i] = t;
    onChange(next);
  };

  const add = () => onChange([...rows, {
    ...Object.fromEntries(fields.map((f) => [f.key, blank(f)])),
    // Identity, not position. A row keeps this for life, so a link to it survives every
    // reorder — see ROW_ID.
    [ROW_ID]: newRowId(),
  }]);

  return (
    <div className="ctl-list">
      {rows.map((row, i) => (
        <details className="ctl-list-row" key={String(row[ROW_ID] ?? i)}>
          <summary className="ctl-list-summary">
            <span className="ctl-list-name">{summaryOf(row, fields)}</span>
            {/* Reordering and removing work on a CLOSED row — that is the whole point of
                a list you can see all of at once. */}
            <span className="ctl-list-btns">
              <button type="button" onClick={(e) => { e.preventDefault(); move(i, -1); }}
                      disabled={i === 0} title="Up">↑</button>
              <button type="button" onClick={(e) => { e.preventDefault(); move(i, 1); }}
                      disabled={i === rows.length - 1} title="Down">↓</button>
              <button type="button"
                      onClick={(e) => { e.preventDefault(); onChange(rows.filter((_, j) => j !== i)); }}
                      title="Remove">✕</button>
            </span>
          </summary>

          <RowFields
            path={path} index={i} fields={fields} row={row} root={root} enums={enums}
            onChange={(k, v) => patch(i, k, v)}
          />
        </details>
      ))}

      <button
        type="button" className="ctl-add"
        disabled={maxItems !== undefined && rows.length >= maxItems}
        onClick={add}
      >
        + {addLabel} {maxItems !== undefined ? `(${rows.length}/${maxItems})` : ''}
      </button>
    </div>
  );
}
